import AppShell from '@/components/AppShell'
import { useEffect, useMemo, useState } from 'react'
import { get } from '@/lib/storage'
import { rowsToLoads, parseCSVText } from '@/lib/importer'
import { addLoads } from '@/lib/mockApi'
import DataTable, { Col } from '@/components/DataTable'
import { useNotifications } from '@/lib/notifications'
import { TBL } from '@/lib/keys'

type Template = {
  id:string; name:string; mill:string; delimiter:string; headerRow:boolean; dateFormat:string;
  mapping: { field:string; column:number }[];
}
const TK = 'import-templates'

export default function ImportMillData(){
  const [templates, setTemplates] = useState<Template[]>([])
  const [tplId, setTplId] = useState<string>('')
  const [csvText, setCsvText] = useState<string>('')
  const [parsed, setParsed] = useState<string[][]>([])
  const [creating, setCreating] = useState(false)
  const { push } = useNotifications()

  useEffect(()=>{ setTemplates(get<Template[]>(TK, [])) },[])
  useEffect(()=>{
    const t = templates.find(x => x.id === tplId)
    if (!t) { setParsed([]); return }
    try {
      if (csvText.trim()) setParsed(parseCSVText(csvText, t.delimiter || ',', t.headerRow))
      else setParsed([])
    } catch (e:any) {
      push({ title:'CSV parse error', body:String(e), level:'error' })
      setParsed([])
    }
  }, [tplId, csvText, templates])

  const previewLoads = useMemo(()=>{
    const t = templates.find(x => x.id === tplId)
    if (!t || parsed.length === 0) return []
    return rowsToLoads(parsed, t.mapping || [], t.dateFormat)
  }, [parsed, tplId, templates])

  const cols: Col[] = [
    { key:'id', label:'ID' },
    { key:'sec', label:'Security Tag' },
    { key:'scale', label:'Scale #' },
    { key:'gross', label:'Gross Wt' },
    { key:'tons', label:'Net Tons' },
    { key:'date', label:'Delivered' },
    { key:'dest', label:'Destination' },
  ]
  const rows = previewLoads.slice(0, 200).map(l => ({
    id: l.id,
    sec: l.securityTag || '',
    scale: l.scaleNumber || '',
    gross: l.grossWeight ?? '',
    tons: l.netTons ?? '',
    date: l.deliveredAt || '',
    dest: l.destination || '',
  }))

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    const text = await f.text()
    setCsvText(text)
  }

  async function createLoads() {
    const t = templates.find(x => x.id === tplId)
    if (!t) { push({title:'Select a template first', level:'warning'}); return }
    if (!previewLoads.length) { push({title:'Nothing to import', level:'warning'}); return }
    setCreating(true)
    try {
      await addLoads(previewLoads)
      push({ title:`Imported ${previewLoads.length} load(s)` })
      setCsvText(''); setParsed([])
    } finally { setCreating(false) }
  }

  return (
    <AppShell title="Import Scale Tickets">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        <div className="card">
          <div className="card-header">Choose Template & Paste CSV</div>
          <div className="card-body grid gap-3">
            <select className="select" value={tplId} onChange={e=>setTplId(e.target.value)}>
              <option value="">— Select a template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name} {t.mill? `• ${t.mill}`:''}</option>)}
            </select>

            <textarea className="input h-40" placeholder="Paste CSV here or use the file picker below…" value={csvText} onChange={e=>setCsvText(e.target.value)} />
            <input type="file" accept=".csv" className="input" onChange={onFile} />

            <div className="flex gap-2">
              <button className="btn" onClick={createLoads} disabled={creating || previewLoads.length===0}>
                {creating ? 'Importing…' : `Create ${previewLoads.length} Loads`}
              </button>
              <a className="btn-secondary" href="/loads/import-templates">Manage Templates</a>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Preview (first 200)</div>
          <div className="card-body">
            {rows.length === 0
              ? <div className="text-sm text-slate-600">No rows to preview.</div>
              : <DataTable columns={cols} rows={rows} filename="import-preview.csv" storageKey={TBL.importPreview} />}
          </div>
        </div>

      </div>
    </AppShell>
  )
}
